<template>
    <section>
        <PageTitle title="Menu Alerts"
                   v-bind:crumbs="[{title:'Home', href:'/orders'},{title:'Menu Alerts', href:'/menu_alerts'}]"/>

        <div class="wrapper wrapper-content animated fadeInRight">
            <div class="row">
                <div class="col-lg-12">
                    <div class="ibox float-e-margins">
                        <div class="ibox-title">
                            <h5>
                                Menu availability changes
                                <span class="label label-primary">{{ count }}</span>
                            </h5>
                            <div class="ibox-tools">
                                <button type="button" class="btn btn-xs btn-primary"
                                        v-bind:disabled="loading" v-on:click="refresh">
                                    <i class="fa fa-refresh" v-bind:class="{'fa-spin': loading}"></i> Refresh
                                </button>
                            </div>
                        </div>
                        <div class="ibox-content">

                            <!-- Filters: stack under 768px via the Bootstrap columns -->
                            <div class="row m-b-sm">
                                <div class="col-md-3 col-sm-6">
                                    <div class="form-group">
                                        <label for="ma-date">Date</label>
                                        <input id="ma-date" type="date" class="form-control"
                                               v-model="date" v-on:change="fetch"/>
                                        <span class="help-block m-b-none">Leave blank for today</span>
                                    </div>
                                </div>
                                <div class="col-md-2 col-sm-6">
                                    <div class="form-group">
                                        <label for="ma-limit">Rows</label>
                                        <select id="ma-limit" class="form-control"
                                                v-model.number="limit" v-on:change="fetch">
                                            <option v-bind:value="50">50</option>
                                            <option v-bind:value="100">100</option>
                                            <option v-bind:value="200">200</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-md-3 col-sm-6">
                                    <div class="form-group">
                                        <label>Status</label>
                                        <div class="btn-group btn-group-justified ma-status-group" role="group"
                                             aria-label="Filter by status">
                                            <div class="btn-group" role="group">
                                                <button type="button" class="btn btn-white"
                                                        v-bind:class="{active: statusFilter === 'all'}"
                                                        v-bind:aria-pressed="statusFilter === 'all'"
                                                        v-on:click="statusFilter = 'all'">All</button>
                                            </div>
                                            <div class="btn-group" role="group">
                                                <button type="button" class="btn btn-white"
                                                        v-bind:class="{active: statusFilter === 'unavailable'}"
                                                        v-bind:aria-pressed="statusFilter === 'unavailable'"
                                                        v-on:click="statusFilter = 'unavailable'">Unavailable</button>
                                            </div>
                                            <div class="btn-group" role="group">
                                                <button type="button" class="btn btn-white"
                                                        v-bind:class="{active: statusFilter === 'available'}"
                                                        v-bind:aria-pressed="statusFilter === 'available'"
                                                        v-on:click="statusFilter = 'available'">Available</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-4 col-sm-6">
                                    <div class="form-group">
                                        <label for="ma-search">Search</label>
                                        <input id="ma-search" type="text" class="form-control"
                                               placeholder="Item, restaurant, branch or message"
                                               v-model.trim="search"/>
                                    </div>
                                </div>
                            </div>

                            <!-- Summary strip -->
                            <div class="ma-summary m-b-sm">
                                <strong>{{ summaryLabel }}</strong>
                                <span class="text-muted" v-if="showingPartial">
                                    &middot; Showing {{ filteredItems.length }} of {{ count }}
                                </span>
                            </div>

                            <Loader v-if="loading && items.length === 0" v-bind:default-height="false"/>

                            <!--
                              Own scroll container rather than .table-responsive: App.vue sets
                              `@media (min-width:1281px){ .table-responsive{overflow-x:initial!important} }`
                              globally, which would disable horizontal scrolling on wide tables.
                            -->
                            <div class="ma-table-scroll" v-else>
                                <table class="table table-striped table-bordered table-hover ma-table">
                                    <thead>
                                    <tr>
                                        <th scope="col">Time</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Message</th>
                                        <th scope="col">Restaurant</th>
                                        <th scope="col">Branch</th>
                                        <th scope="col">Type</th>
                                        <th scope="col">Source</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-if="error">
                                        <td colspan="7" class="ma-error">
                                            <i class="fa fa-exclamation-triangle text-danger"></i>
                                            <span class="text-danger">{{ error }}</span>
                                            <button type="button" class="btn btn-xs btn-danger m-l-sm"
                                                    v-bind:disabled="loading" v-on:click="fetch">
                                                <i class="fa fa-refresh"></i> Retry
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-for="item in filteredItems" v-bind:key="item.notification_id"
                                        v-bind:class="{'ma-row-new': item.isNew}">
                                        <td class="text-nowrap">
                                            {{ formatTime(item.occurred_at) }}
                                            <small class="text-muted ma-sub">{{ relativeTime(item.occurred_at) }}</small>
                                        </td>
                                        <td class="text-center">
                                            <span class="label"
                                                  v-bind:class="item.available ? 'label-primary' : 'label-danger'">
                                                {{ item.available ? 'Available' : 'Unavailable' }}
                                            </span>
                                        </td>
                                        <td class="ma-message">{{ item.message }}</td>
                                        <td>{{ item.restaurant_name || 'N/A' }}</td>
                                        <td>{{ item.branch_area || 'N/A' }}</td>
                                        <td class="text-nowrap">{{ typeLabel(item) }}</td>
                                        <td class="text-center">
                                            <span class="label"
                                                  v-bind:class="item.source === 'KITCHEN' ? 'label-warning' : 'label-info'">
                                                {{ sourceLabel(item) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="filteredItems.length === 0 && !error">
                                        <td colspan="7" class="text-center ma-empty">
                                            <template v-if="items.length === 0">
                                                <i class="fa fa-check-circle fa-4x text-navy"></i>
                                                <h3>{{ emptyTitle }}</h3>
                                                <p class="text-muted">
                                                    Items and servings switched on or off by the kitchen or the
                                                    portal will appear here.
                                                </p>
                                            </template>
                                            <template v-else>
                                                <i class="fa fa-filter fa-3x text-muted"></i>
                                                <h3>No alerts match your filters</h3>
                                                <button type="button" class="btn btn-sm btn-white"
                                                        v-on:click="clearFilters">Clear filters</button>
                                            </template>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    /* eslint-disable */
    import PageTitle from '@/components/layout_components/PageTitle'
    import Loader from '@/components/utils/Loader'

    export default {
        name: "MenuAlerts",
        components: {PageTitle, Loader},
        data() {
            return {
                date: '',            // yyyy-mm-dd from the native picker, '' = today
                limit: 50,
                statusFilter: 'all', // all | unavailable | available
                search: '',
                now: Date.now(),     // ticks so the relative times stay current
                tickTimer: null
            }
        },
        computed: {
            items() { return this.$store.state.menuAlerts.items },
            count() { return this.$store.state.menuAlerts.count },
            viewedDay() { return this.$store.state.menuAlerts.viewedDay },
            loading() { return this.$store.state.menuAlerts.loading },
            error() { return this.$store.state.menuAlerts.error },

            filteredItems() {
                const term = (this.search || '').toLowerCase();
                return this.items.filter(item => {
                    if (this.statusFilter === 'unavailable' && item.available) return false;
                    if (this.statusFilter === 'available' && !item.available) return false;
                    if (!term) return true;
                    return [item.message, item.restaurant_name, item.branch_area,
                        item.menu_item_name, item.serving_name]
                        .some(value => value && String(value).toLowerCase().indexOf(term) !== -1);
                });
            },
            filtersActive() {
                return this.statusFilter !== 'all' || !!this.search;
            },
            // Truncated by the limit, or hidden by the client-side filters.
            showingPartial() {
                return this.items.length < this.count
                    || (this.filtersActive && this.filteredItems.length < this.items.length);
            },
            summaryLabel() {
                return this.date
                    ? this.count + ' on ' + (this.viewedDay || this.toApiDate(this.date))
                    : this.count + ' today';
            },
            emptyTitle() {
                return this.date ? 'No menu changes on ' + (this.viewedDay || this.toApiDate(this.date))
                    : 'No menu changes today';
            }
        },
        mounted() {
            this.$store.commit('menuAlerts/setPageOpen', true);
            this.fetch();
            // Local clock only, for the relative times. Makes no requests.
            this.tickTimer = setInterval(() => { this.now = Date.now() }, 30000);
        },
        beforeDestroy() {
            clearInterval(this.tickTimer);
            this.$store.commit('menuAlerts/setPageOpen', false);
        },
        methods: {
            /**
             * yyyy-mm-dd (native date input) -> dd-MM-yyyy (API). Blank stays undefined so the
             * backend picks "today"; the client never computes the current date.
             */
            toApiDate(value) {
                if (!value) return undefined;
                const parts = value.split('-');
                if (parts.length !== 3) return undefined;
                return parts[2] + '-' + parts[1] + '-' + parts[0];
            },
            fetch() {
                return this.$store.dispatch('menuAlerts/fetchHistory', {
                    date: this.toApiDate(this.date),
                    limit: this.limit
                });
            },
            refresh() {
                if (this.loading) return;
                this.fetch();
            },
            clearFilters() {
                this.statusFilter = 'all';
                this.search = '';
            },
            formatTime(value) {
                return value ? this.$moment(value).format('DD/MM/YYYY hh:mm:ss A') : 'N/A';
            },
            relativeTime(value) {
                return value ? this.$moment(value).from(this.now) : '';
            },
            typeLabel(item) {
                return item.target_type === 'SERVING' ? 'Serving' : 'Item';
            },
            sourceLabel(item) {
                if (item.source === 'KITCHEN') return 'Kitchen';
                if (item.source === 'PORTAL') return 'Portal';
                return item.source || 'N/A';
            }
        }
    }
</script>

<style scoped>
    .ma-summary { font-size: 13px; color: #676a6c; }

    .ma-status-group .btn.active {
        background-color: #1ab394;
        border-color: #1ab394;
        color: #ffffff;
        box-shadow: none;
    }

    /*
      Own scroll container rather than Bootstrap's .table-responsive, which App.vue
      neutralises above 1281px with an !important override.
    */
    .ma-table-scroll {
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
    }

    .ma-table {
        margin-bottom: 0;
        font-size: 12px;
    }

    .ma-table > thead > tr > th {
        white-space: nowrap;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: .3px;
        color: #9a9da0;
    }

    .ma-table > tbody > tr > td {
        vertical-align: middle;
        padding: 7px 8px;
    }

    .text-nowrap { white-space: nowrap; }

    .ma-message { font-weight: 600; min-width: 220px; }

    /* Relative time tucked under the absolute one. */
    .ma-sub {
        display: block;
        font-size: 10px;
        line-height: 1.3;
    }

    .ma-error { padding: 14px 10px; }
    .ma-empty { padding: 45px 15px; }
    .ma-empty h3 { margin: 14px 0 6px; }

    /* animate.css's flash animates opacity, so the highlight needs its own keyframes. */
    @keyframes maRowNew {
        from { background-color: rgba(26, 179, 148, .40) }
        to { background-color: transparent }
    }

    .ma-row-new > td { animation: maRowNew 2.4s ease-out 1; }

    @media (prefers-reduced-motion: reduce) {
        .ma-row-new > td { animation: none; }
    }
</style>
